/**
 * EmbeddingStatusService — Provider for document embedding/indexing status (sl-amc0, sl-amc0.14)
 *
 * SOLID: D — Dependency Inversion
 * BlockSuite defines the interface; AFFiNE frontend implements it with WebSocket + GraphQL.
 *
 * Used by attachment blocks to show an indexing badge on the edgeless canvas.
 *
 * sl-amc0.14: Added WebSocket-based live status updates + batch progress.
 */
import type { Container } from '@blocksuite/global/di';
import { createIdentifier } from '@blocksuite/global/di';
import type { BlockStdScope } from '@blocksuite/std';
import type { ExtensionType } from '@blocksuite/store';
import { Extension } from '@blocksuite/store';

// =============================================================================
// Event Types (mirror backend EmbeddingStatusGateway events)
// =============================================================================

export interface EmbeddingDocStatusEvent {
  workspaceId: string;
  docId: string;
  status: 'indexing' | 'indexed' | 'failed';
  chunksIndexed?: number;
  chunksTotal?: number;
  timestamp: number;
}

export interface EmbeddingWorkspaceProgressEvent {
  workspaceId: string;
  docsIndexed: number;
  docsTotal: number;
  timestamp: number;
}

export type EmbeddingIndexReviewSubjectKind =
  | 'attachment-block'
  | 'image-block'
  | 'database-file-cell';

export const DATABASE_FILE_CELL_INDEX_REVIEW_COPY_EVENT =
  'streamlain:database-file-cell-index-review-copy';

export interface DatabaseFileCellIndexReviewCopyEventDetail {
  workspaceId: string;
  docId: string;
  databaseId: string;
  sourcePropertyId: string;
  targetPropertyId: string;
  rows: {
    rowId: string;
    refId: string;
    blobId: string;
  }[];
}

export function dispatchDatabaseFileCellIndexReviewCopy(
  detail: DatabaseFileCellIndexReviewCopyEventDetail
): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent(DATABASE_FILE_CELL_INDEX_REVIEW_COPY_EVENT, { detail })
  );
}

export type EmbeddingIndexReviewField =
  | 'visualDescription'
  | 'searchTerms'
  | 'manualSearchAdditions'
  | 'ocrText'
  | 'pageText';

export type EmbeddingIndexReviewRegenerableField = Exclude<
  EmbeddingIndexReviewField,
  'manualSearchAdditions'
>;

export interface EmbeddingIndexReviewLocator {
  workspaceId: string;
  docId: string;
  subjectKind: EmbeddingIndexReviewSubjectKind;
  blockId?: string | null;
  databaseId?: string | null;
  rowId?: string | null;
  propertyId?: string | null;
  refId?: string | null;
  blobId: string;
  pageIndex?: number | null;
}

export interface EmbeddingIndexReviewCurrentValue {
  id: string | null;
  field: EmbeddingIndexReviewField;
  pageIndex: number | null;
  value: unknown;
  updatedAt: string | null;
}

export interface EmbeddingIndexReviewSubjectSummary {
  id: string | null;
  locator: EmbeddingIndexReviewLocator;
  canWrite: boolean;
  currentValues: EmbeddingIndexReviewCurrentValue[];
  imageAnalysis: {
    state: 'complete' | 'missing';
    availability: 'available' | 'paused_no_credits' | 'unavailable';
  } | null;
}

export interface EmbeddingIndexReviewRegenerationResult {
  queued: true;
  jobId: string;
}

export interface EmbeddingIndexReviewRegenerationUnavailable {
  queued: false;
  unavailable: {
    code: 'INDEX_REVIEW_REGENERATION_UNAVAILABLE';
    reason:
      | 'image-ai-unavailable'
      | 'ocr-ai-unavailable'
      | 'text-ai-unavailable';
    message: string;
  };
}

export type EmbeddingIndexReviewRegenerationResponse =
  | EmbeddingIndexReviewRegenerationResult
  | EmbeddingIndexReviewRegenerationUnavailable;

export function createBlockIndexReviewLocator(input: {
  workspaceId: string;
  docId: string;
  subjectKind: Extract<
    EmbeddingIndexReviewSubjectKind,
    'attachment-block' | 'image-block'
  >;
  blockId: string;
  blobId: string | null | undefined;
  pageIndex?: number | null;
}): EmbeddingIndexReviewLocator | null {
  if (!input.blobId) return null;
  return {
    workspaceId: input.workspaceId,
    docId: input.docId,
    subjectKind: input.subjectKind,
    blockId: input.blockId,
    blobId: input.blobId,
    pageIndex: input.pageIndex ?? null,
  };
}

export function createDatabaseFileCellIndexReviewLocator(input: {
  workspaceId: string;
  docId: string;
  databaseId: string;
  rowId: string;
  propertyId: string;
  refId: string;
  blobId: string | null | undefined;
  pageIndex?: number | null;
}): EmbeddingIndexReviewLocator | null {
  if (!input.blobId) return null;
  return {
    workspaceId: input.workspaceId,
    docId: input.docId,
    subjectKind: 'database-file-cell',
    databaseId: input.databaseId,
    rowId: input.rowId,
    propertyId: input.propertyId,
    refId: input.refId,
    blobId: input.blobId,
    pageIndex: input.pageIndex ?? null,
  };
}

export function copyBlockIndexReviewSubject(input: {
  std: BlockStdScope;
  subjectKind: Extract<
    EmbeddingIndexReviewSubjectKind,
    'attachment-block' | 'image-block'
  >;
  sourceBlockId: string;
  targetBlockId: string;
  blobId: string | null | undefined;
}): void {
  const service = input.std.getOptional<IEmbeddingStatusService>(
    EmbeddingStatusProvider
  );
  if (!service?.copyIndexReviewSubject || !input.blobId) return;

  const locatorBase = {
    workspaceId: input.std.store.workspace.id,
    docId: input.std.store.id,
    subjectKind: input.subjectKind,
    blobId: input.blobId,
    pageIndex: null,
  } satisfies Omit<EmbeddingIndexReviewLocator, 'blockId'>;

  void service
    .copyIndexReviewSubject(
      {
        ...locatorBase,
        blockId: input.sourceBlockId,
      },
      {
        ...locatorBase,
        blockId: input.targetBlockId,
      }
    )
    .catch((error: unknown) => {
      console.warn('Failed to copy index review metadata', error);
    });
}

export function copyDatabaseFileCellIndexReviewSubject(input: {
  std: BlockStdScope;
  databaseId: string;
  sourceRowId: string;
  sourcePropertyId: string;
  sourceRefId: string;
  targetRowId: string;
  targetPropertyId: string;
  targetRefId: string;
  blobId: string | null | undefined;
}): void {
  const service = input.std.getOptional<IEmbeddingStatusService>(
    EmbeddingStatusProvider
  );
  if (!service?.copyIndexReviewSubject || !input.blobId) return;

  const locatorBase = {
    workspaceId: input.std.store.workspace.id,
    docId: input.std.store.id,
    subjectKind: 'database-file-cell',
    databaseId: input.databaseId,
    blobId: input.blobId,
    pageIndex: null,
  } satisfies Omit<
    EmbeddingIndexReviewLocator,
    'propertyId' | 'refId' | 'rowId'
  >;

  void service
    .copyIndexReviewSubject(
      {
        ...locatorBase,
        rowId: input.sourceRowId,
        propertyId: input.sourcePropertyId,
        refId: input.sourceRefId,
      },
      {
        ...locatorBase,
        rowId: input.targetRowId,
        propertyId: input.targetPropertyId,
        refId: input.targetRefId,
      }
    )
    .catch((error: unknown) => {
      console.warn('Failed to copy database file index review metadata', error);
    });
}

export class EmbeddingStatusUnavailableError extends Error {
  readonly code = 'EMBEDDING_STATUS_UNAVAILABLE';

  constructor(message = 'Embedding status is unavailable') {
    super(message);
    this.name = 'EmbeddingStatusUnavailableError';
  }
}

export function isEmbeddingStatusUnavailableError(
  error: unknown
): error is EmbeddingStatusUnavailableError {
  return (
    error instanceof EmbeddingStatusUnavailableError ||
    (typeof error === 'object' &&
      error !== null &&
      'code' in error &&
      error.code === 'EMBEDDING_STATUS_UNAVAILABLE')
  );
}

export class EmbeddingCreditsRequiredError extends Error {
  readonly code = 'EMBEDDING_CREDITS_REQUIRED';

  constructor(
    message = 'Image analysis is paused because no credits are available'
  ) {
    super(message);
    this.name = 'EmbeddingCreditsRequiredError';
  }
}

export function isEmbeddingCreditsRequiredError(
  error: unknown
): error is EmbeddingCreditsRequiredError {
  return (
    error instanceof EmbeddingCreditsRequiredError ||
    (typeof error === 'object' &&
      error !== null &&
      'code' in error &&
      error.code === 'EMBEDDING_CREDITS_REQUIRED')
  );
}

export type EmbeddingStatusLabelKey =
  | 'indexedForSearch'
  | 'clickToIndexForSearch'
  | 'indexingInProgress'
  | 'indexingDocuments'
  | 'indexingStarted'
  | 'indexingFailed'
  | 'indexingFailedHint'
  | 'indexedSuccessfully'
  | 'reindex'
  | 'checkAgain'
  | 'reviewDetails'
  | 'searchIndex'
  | 'indexStatusIndexed'
  | 'indexStatusNotIndexed'
  | 'indexStatusIndexing'
  | 'indexStatusFailed'
  | 'indexStatusDisabled'
  | 'indexStatusUnavailable'
  | 'indexStatusUnavailableHint'
  | 'indexingPaused'
  | 'indexingPausedHint'
  | 'imageAnalysisPaused'
  | 'imageAnalysisPausedHint'
  | 'imageAnalysisFailed'
  | 'imageAnalysisFailedHint'
  | 'existingIndexPreserved'
  | 'manageCredits'
  | 'reviewSource'
  | 'reviewSourceGenerated'
  | 'reviewSourceReviewed'
  | 'reviewSourceEdited'
  | 'reviewSourceStale'
  | 'reviewPanelTitle'
  | 'reviewPanelClose'
  | 'reviewSectionDescription'
  | 'reviewSectionSearchTerms'
  | 'reviewSectionExtractedText'
  | 'reviewSectionManualAdditions'
  | 'reviewGeneratedValue'
  | 'reviewUserValue'
  | 'reviewNoValue'
  | 'reviewSave'
  | 'reviewSaving'
  | 'reviewSaved'
  | 'reviewCancel'
  | 'reviewRegenerateWithHint'
  | 'reviewRegenerateAction'
  | 'reviewRegenerating'
  | 'reviewHintPlaceholder'
  | 'reviewReplaceWarning'
  | 'reviewReadOnly'
  | 'reviewLoadFailed'
  | 'reviewConflict'
  | 'reviewQueued'
  | 'notSearchable'
  | 'notIndexedHint'
  | 'embeddingDisabledTitle'
  | 'embeddingDisabledHint';

// =============================================================================
// Service Interface
// =============================================================================

export interface IEmbeddingStatusService {
  /** Check if the parent document or a concrete block has indexed search content */
  hasEmbeddings(docId: string, blockId?: string): Promise<boolean>;
  /** Trigger re-indexing for a document */
  reindex(
    docId: string,
    locator?: EmbeddingIndexReviewLocator | null
  ): Promise<void>;
  openCreditSettings?(): void;

  // sl-amc0.14: Live WebSocket subscriptions
  /** Subscribe to doc-level embedding status changes. Returns unsubscribe function. */
  onDocStatus(callback: (event: EmbeddingDocStatusEvent) => void): () => void;
  /** Subscribe to workspace-level batch progress. Returns unsubscribe function. */
  onWorkspaceProgress(
    callback: (event: EmbeddingWorkspaceProgressEvent) => void
  ): () => void;
  /** Current workspace batch progress (null if no batch in progress) */
  getWorkspaceProgress(): EmbeddingWorkspaceProgressEvent | null;
  /** Localized UI labels for embedding badges and toasts */
  getLabel?(
    key: EmbeddingStatusLabelKey,
    params?: Record<string, number | string>
  ): string;
  /** Load user-reviewable index metadata for a concrete file occurrence. */
  getIndexReviewSubject?(
    locator: EmbeddingIndexReviewLocator
  ): Promise<EmbeddingIndexReviewSubjectSummary>;
  /** Save directly editable user review metadata for one field. */
  saveIndexReviewField?(
    locator: EmbeddingIndexReviewLocator,
    field: EmbeddingIndexReviewField,
    value: unknown,
    options?: { expectedActiveVersionId?: string | null }
  ): Promise<void>;
  /** Queue targeted AI/OCR regeneration with a user hint. */
  regenerateIndexReviewField?(
    locator: EmbeddingIndexReviewLocator,
    field: EmbeddingIndexReviewRegenerableField,
    userHint: string,
    options?: {
      mediaType?: string | null;
      confirmed?: boolean;
      expectedActiveVersionId?: string | null;
    }
  ): Promise<EmbeddingIndexReviewRegenerationResponse>;
  /** Copy active review metadata when a file occurrence is duplicated in-place. */
  copyIndexReviewSubject?(
    sourceLocator: EmbeddingIndexReviewLocator,
    targetLocator: EmbeddingIndexReviewLocator
  ): Promise<void>;
  /**
   * Workspace embedding/index policy: whether semantic indexing is enabled for
   * the workspace. `null` = unknown (treated as enabled by consumers).
   */
  isEmbeddingEnabled?(): boolean | null;
  /**
   * Subscribe to workspace embedding-policy changes. Returns unsubscribe fn.
   */
  onEnabledChange?(callback: (enabled: boolean | null) => void): () => void;
  /** Disconnect and cleanup (called on component/service dispose) */
  dispose(): void;
}

const fallbackLabels: Record<EmbeddingStatusLabelKey, string> = {
  indexedForSearch: 'Searchable',
  clickToIndexForSearch: 'Index',
  indexingInProgress: 'Indexing...',
  indexingDocuments: 'Indexing {{indexed}}/{{total}} documents...',
  indexingStarted: 'Indexing started...',
  indexingFailed: 'Indexing failed',
  indexingFailedHint:
    'The last indexing attempt failed. Try again to make this content searchable.',
  indexedSuccessfully: 'Indexed successfully',
  reindex: 'Index again',
  checkAgain: 'Check again',
  reviewDetails: 'Review details',
  searchIndex: 'Search index',
  indexStatusIndexed: 'Indexed',
  indexStatusNotIndexed: 'Not indexed',
  indexStatusIndexing: 'Indexing',
  indexStatusFailed: 'Failed',
  indexStatusDisabled: 'Disabled',
  indexStatusUnavailable: 'Service unavailable',
  indexStatusUnavailableHint:
    'The indexing service is currently unavailable. Your content is not lost.',
  indexingPaused: 'Indexing paused',
  indexingPausedHint:
    'No credits are available. Add credits to make this content searchable.',
  imageAnalysisPaused: 'Image analysis paused',
  imageAnalysisPausedHint:
    'No credits are available. Caption and filename remain searchable.',
  imageAnalysisFailed: 'Image analysis failed',
  imageAnalysisFailedHint:
    'Caption and filename remain searchable. Try image analysis again later.',
  existingIndexPreserved:
    'The existing searchable version remains available until replacement succeeds.',
  manageCredits: 'Manage credits',
  reviewSource: 'Review',
  reviewSourceGenerated: 'Generated',
  reviewSourceReviewed: 'Reviewed',
  reviewSourceEdited: 'Edited',
  reviewSourceStale: 'Outdated',
  reviewPanelTitle: 'Index metadata',
  reviewPanelClose: 'Close',
  reviewSectionDescription: 'Description',
  reviewSectionSearchTerms: 'Search terms',
  reviewSectionExtractedText: 'Extracted text',
  reviewSectionManualAdditions: 'Manual search additions',
  reviewGeneratedValue: 'Generated',
  reviewUserValue: 'User value',
  reviewNoValue: 'No value yet',
  reviewSave: 'Save',
  reviewSaving: 'Saving...',
  reviewSaved: 'Saved',
  reviewCancel: 'Cancel',
  reviewRegenerateWithHint: 'Improve with AI',
  reviewRegenerateAction: 'Regenerate with AI',
  reviewRegenerating: 'Regenerating...',
  reviewHintPlaceholder: 'Tell AI what to improve...',
  reviewReplaceWarning:
    'The result replaces the current value and is saved automatically.',
  reviewReadOnly: 'Read-only',
  reviewLoadFailed: 'Index metadata could not be loaded.',
  reviewConflict:
    'Index metadata changed in the meantime. The latest version was loaded.',
  reviewQueued: 'Regeneration started. The result is saved automatically.',
  notSearchable: 'Not searchable',
  notIndexedHint: 'This content is not indexed for search yet.',
  embeddingDisabledTitle: 'Indexing disabled',
  embeddingDisabledHint:
    'Full-text search is disabled for this workspace. An admin can enable it in the workspace settings.',
};

export function getEmbeddingStatusLabel(
  service: Pick<IEmbeddingStatusService, 'getLabel'> | null | undefined,
  key: EmbeddingStatusLabelKey,
  params?: Record<string, number | string>
): string {
  const label = service?.getLabel?.(key, params) ?? fallbackLabels[key];
  if (!params) return label;
  return Object.entries(params).reduce(
    (text, [paramKey, paramValue]) =>
      text.replaceAll(`{{${paramKey}}}`, String(paramValue)),
    label
  );
}

export const EmbeddingStatusProvider =
  createIdentifier<IEmbeddingStatusService>('EmbeddingStatusService');

/**
 * Extension factory — wraps an existing implementation as a BlockSuite Extension.
 */
export function EmbeddingStatusExtension(
  service: IEmbeddingStatusService
): ExtensionType {
  class EmbeddingStatusExtensionImpl extends Extension {
    static override setup(di: Container) {
      di.addImpl(EmbeddingStatusProvider, () => service);
    }
  }
  return EmbeddingStatusExtensionImpl;
}
